/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useState
} from 'react';
import {useDispatch} from 'react-redux';
import {makeStyles} from '@material-ui/core';
import isEmpty from 'lodash.isempty';
import {translate} from '@ion/core/src/locale/locale';
import {Account, GenericObject} from '@ion/core/src/types/entities';
import {isValidEmail} from '@ion/core/src/utils/Validators';
import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
import FormItem from '@ion/dls/src/components/forms/FormItem';
import FormSection from '@ion/dls/src/components/forms/FormSection';
import FormTextField from '@ion/dls/src/components/forms/FormTextField';
import {ButtonSpinner} from '@ion/dls/src/components/loading/ButtonSpinner';
import Switch from '@ion/dls/src/components/switch/Switch';
import {CarbEditorFields, deserializeAddress} from '@ion/dls/src/editors/GroupEditorUtils';
import {validate} from '@ion/dls/src/hooks/useFormState';
import IonSnackbar from '@ion/dls/src/notifications/IonSnackbar';
import Render from '@ion/dls/src/render/Render';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {useDeleteTester} from '@ion/query/src/hooks/useDeleteTester';
import {useEmissionTester} from '@ion/query/src/hooks/useEmissionTester';
import {toggleUserPrompt} from '../../../store/actions/user-prompt';
import AccountTooltip from './AccountTooltip';

const useFormStyle = makeStyles((theme) => ({
  formSection : {
    paddingTop : '0px !important'
  },
  root : {
    display       : 'flex',
    flexDirection : 'column'
  },
  actions : {
    display    : 'flex',
    alignSelf  : 'flex-end',
    alignItems : 'center'
  },
  saveButtonContainer : {
    margin : '0px !important',
    width  : '122px'
  },
  formSectionTitle : {
    ...dlsAlt('typography', 'cta3'),
    textTransform : 'uppercase',
    paddingLeft   : '0px'
  },
  switch : {
    display                        : 'flex',
    alignItems                     : 'center',
    gap                            : 8,
    [theme.breakpoints.down('sm')] : {
      display : 'block'
    }
  },
  overridesSwitches : {
    padding : '10px 5px'
  },
  formSectionContent : {
    justifyContent : 'flex-start'
  },
  label : {
    width : 370
  },
  carbToggle : {
    display         : 'flex',
    alignItems      : 'center',
    justifyContent  : 'space-between',
    backgroundColor : dlsColor('white'),
    height          : 60,
    marginTop       : 17,
    borderBottom    : `2px solid ${dlsColor('grey6')}`
  },
  fieldLabel : {
    display    : 'flex',
    alignItems : 'center',
    width      : '100%',
    font       : '14px Roboto'
  },
  formContainer : {
    backgroundColor : dlsColor('white'),
    width           : '100%',
    padding         : 20
  },
  form : {
    display        : 'flex',
    flexDirection  : 'column',
    justifyContent : 'center',
    alignItems     : 'center',
    flexWrap       : 'wrap'
  },
  required : {
    color  : dlsColor('caBlue'),
    margin : 2
  },
  carbDelete : {
    display         : 'flex',
    justifyContent  : 'center',
    alignItems      : 'center',
    backgroundColor : dlsColor('white'),
    borderTop       : `2px solid ${dlsColor('grey6')}`
  },
  deleteBorder : {
    border          : `2px solid ${dlsColor('caBlue')}`,
    backgroundColor : dlsColor('white'),
    color           : dlsColor('caBlue'),
    '&:focus'       : {
      color : `${dlsColor('caBlue')} !important`
    }
  },
  deleteActive : {
    border          : `2px solid ${dlsColor('caBlue')}`,
    backgroundColor : dlsColor('caBlue'),
    color           : `${dlsColor('white')} !important`,
    '&:hover'       : {
      backgroundColor : `${dlsColor('caBlue')} !important`
    }
  },
  deleteMsg : {
    marginLeft : '10px',
    font       : '14px Roboto'
  }
}));

interface CarbSettingsProps {
  isEdit?: boolean;
  isView?: boolean;
  formState?: any;
  setFormState?: Function;
  setShowCarb: Function;
  showCarb: boolean;
  account: Account;
}

export const CarbSettings = ({
  account,
  isEdit,
  isView,
  formState,
  setFormState,
  showCarb,
  setShowCarb
}: CarbSettingsProps) => {
  const classes                                 = useFormStyle();
  const dispatch                                = useDispatch();
  const [showDeletePrompt, setShowDeletePrompt] = useState(false);
  const {emissionTester, isLoading, refresh}    = useEmissionTester(
    {id: account?.id}, ((isEdit || isView)));
  const {
    deleteTester,
    isLoading : isDeleting,
    isSuccess: isDeleteSuccess
  }                                             = useDeleteTester();
  const hasEmissionTester                       = !isEmpty(emissionTester);

  useEffect(() => {
    if (isDeleteSuccess) {
      refresh();
    }
  }, [isDeleteSuccess]);
  
  useEffect(() => {
    if (!isLoading && (isEdit || isView)) {
      setFormState?.(deserializeAddress(emissionTester, false));
    }
  }, [showCarb, emissionTester, isEdit, isLoading, isView]);

  const onToggleChange = useCallback(() => {
    setShowCarb(!showCarb);
  }, [showCarb]);

  useEffect(() => {
    setShowCarb(hasEmissionTester);
  }, [hasEmissionTester, setShowCarb]);

  const onFieldChange = useCallback(({target: {value, name}}) => {
    let error = false;
    if (name === CarbEditorFields.COMPLIANCE_SPECIALIST_EMAIL) {
      error = !validate(value, isValidEmail);
    }
    setFormState({
      [name] : {
        value    : value,
        error    : error,
        required : true
      }
    });
  }, [formState]);

  const onDelete = useCallback(() => {
    setShowDeletePrompt(true);
  }, []);

  const onDismiss = useCallback(() => {
    setShowDeletePrompt(false);
  }, []);

  const onDeleteConfirm = useCallback(() => {
    setShowDeletePrompt(false);
    deleteTester(account?.id, {
      onSuccess : () => {
        dispatch(toggleUserPrompt({
          message          : translate('common:editors.delete.success', {entity: translate('common:entityLabels.emissionTester')}),
          autoHideDuration : 1000
        }));
      },
      onError : () => {
        dispatch(toggleUserPrompt({
          message     : translate('common:editors.delete.failure', {entity: translate('common:entityLabels.emissionTester')}),
          actionLabel : 'Ok'
        }));
      }
    });
  }, []);

  return (
    <div>
      <FormSection required
        stickyTitle
        overrideClasses={{formSectionContent: classes.formSectionContent}}
        title={translate('admin-groups:carb.carbTitle')}
        legend={
          <AccountTooltip title={translate('admin-groups:carb.tooltip')} />
        }>
        <FormItem fullWidth className={classes.carbToggle}>
          <h1 className={classes.formSectionTitle}>{translate('admin-groups:carb.carbLabel')}</h1>
          <div className={classes.switch}>
            <div className={classes.overridesSwitches}>
              <Switch onChange={onToggleChange}
                checked={showCarb} />
            </div>
          </div>
        </FormItem>
        <Render.If condition={showCarb}>
          <div className={classes.formContainer}>
            <div className={classes.form}>
              <FormItem customWidth={8}>
                <div className={classes.fieldLabel}>
                  <span className={classes.label}>
                    {translate('admin-groups:carb.carbFields.complianceSpecialistId.label')}
                    <span className={classes.required}>*</span></span>
                  <FormTextField required
                    disabled={isView}
                    placeholder={translate('admin-groups:carb.carbFields.complianceSpecialistId.placeholder')}
                    name={CarbEditorFields.COMPLIANCE_SPECIALIST_ID}
                    maxLength={64}
                    value={formState[CarbEditorFields.COMPLIANCE_SPECIALIST_ID]?.value}
                    onChange={onFieldChange}
                    hasError={formState[CarbEditorFields.COMPLIANCE_SPECIALIST_ID]?.error}
                    errorText={translate('admin-groups:carb.carbFields.complianceSpecialistId.error')}/>
                </div>
              </FormItem>
              <FormItem customWidth={8}>
                <div className={classes.fieldLabel}>
                  <span className={classes.label}>
                    {translate('admin-groups:carb.carbFields.complianceSpecialistName.label')}
                    <span className={classes.required}>*</span></span>
                  <FormTextField required
                    disabled={isView}
                    placeholder={translate('admin-groups:carb.carbFields.complianceSpecialistName.placeholder')}
                    name={CarbEditorFields.COMPLIANCE_SPECIALIST_NAME}
                    maxLength={64}
                    value={formState[CarbEditorFields.COMPLIANCE_SPECIALIST_NAME]?.value}
                    onChange={onFieldChange}
                    hasError={formState[CarbEditorFields.COMPLIANCE_SPECIALIST_NAME]?.error}
                    errorText={translate('admin-groups:carb.carbFields.complianceSpecialistName.error')}/>
                </div>
              </FormItem>
              <FormItem customWidth={8}>
                <div className={classes.fieldLabel}>
                  <span className={classes.label}>
                    {translate('admin-groups:carb.carbFields.complianceSpecialistEmail.label')}
                    <span className={classes.required}>*</span></span>
                  <FormTextField required
                    disabled={isView}
                    placeholder={translate('admin-groups:carb.carbFields.complianceSpecialistEmail.placeholder')}
                    name={CarbEditorFields.COMPLIANCE_SPECIALIST_EMAIL}
                    maxLength={64}
                    value={formState[CarbEditorFields.COMPLIANCE_SPECIALIST_EMAIL]?.value}
                    onChange={onFieldChange}
                    hasError={formState[CarbEditorFields.COMPLIANCE_SPECIALIST_EMAIL]?.error}
                    errorText={translate('admin-groups:carb.carbFields.complianceSpecialistEmail.error')}/>
                </div>
              </FormItem>
            </div>
          </div>
        </Render.If>
        <Render.If condition={showCarb && !!emissionTester?.testerId && !isView}>
          <FormItem fullWidth className={classes.carbDelete}>
            <TertiaryButton className={!showDeletePrompt ? classes.deleteBorder : classes.deleteActive}
              noUnderline
              noUnderlineOnFocus
              onClick={onDelete}>
              {isDeleting
                ? <ButtonSpinner />
                : <>{translate('common:delete')}</>}
            </TertiaryButton>
            <div className={classes.deleteMsg}>{translate('admin-groups:carb.deleteMsg')}</div>
          </FormItem>
        </Render.If>
      </FormSection>
      <IonSnackbar show={showDeletePrompt}
        message={translate('admin-groups:carb.deleteConfirm')}
        customDismissLabel={translate('common:cancel')}
        onDismiss={onDismiss}
        onAction={onDeleteConfirm}
        actionLabel={translate('common:delete')} />
    </div>
  );
};

export default CarbSettings;
